import { useState } from "react";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { PersonDialog } from "./PersonDialog";

// Single-select searchable picker of active people (inactive shown only if currently selected). Optional inline "Add person".
export const PersonPicker = ({ people, value, onChange, placeholder = "Select person", allowAdd = true, onAdded, allowNone = false, testid, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [add, setAdd] = useState(false);
  const [q, setQ] = useState("");
  const selected = people.find((p) => p.id === value);
  const options = people.filter((p) => p.active || p.id === value);
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" role="combobox" data-testid={testid} className={`flex h-9 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm ${className}`}>
            <span className={`truncate ${selected ? "" : "text-slate-400"}`}>{selected ? `${selected.name}${selected.active ? "" : " (inactive)"}` : placeholder}</span>
            <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[260px]" align="start">
          <Command>
            <CommandInput placeholder="Search people…" value={q} onValueChange={setQ} data-testid={testid ? `${testid}-search` : undefined} />
            <CommandList>
              <CommandEmpty>No matching person.</CommandEmpty>
              <CommandGroup>
                {allowNone && <CommandItem value="__none" onSelect={() => { onChange(null); setOpen(false); }}>— None</CommandItem>}
                {options.map((p) => (
                  <CommandItem key={p.id} value={`${p.name} ${p.department || ""}`} onSelect={() => { onChange(p.id); setOpen(false); }} data-testid={testid ? `${testid}-option-${p.id}` : undefined}>
                    <Check className={`mr-2 h-4 w-4 ${p.id === value ? "opacity-100" : "opacity-0"}`} />
                    <span className="truncate">{p.name}</span>{p.department && <span className="ml-2 text-xs text-slate-400 truncate">{p.department}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
              {allowAdd && (
                <CommandGroup>
                  <CommandItem value={`__add ${q}`} onSelect={() => { setOpen(false); setAdd(true); }} className="text-teal-700" data-testid={testid ? `${testid}-add-person` : undefined}>
                    <Plus className="mr-2 h-4 w-4" /> Add team member{q ? ` "${q}"` : ""}
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {allowAdd && <PersonDialog open={add} onOpenChange={setAdd} initialName={q} onSaved={(p) => { onAdded?.(p); onChange(p.id); setQ(""); }} />}
    </>
  );
};

// Multi-select of team members with optional per-project role.
export const TeamPicker = ({ people, value = [], onChange, onAdded, exclude = [], testid = "team-picker" }) => {
  const [pick, setPick] = useState(null);
  const ids = new Set(value.map((t) => t.person_id));
  const available = people.filter((p) => !ids.has(p.id));
  const byId = Object.fromEntries(people.map((p) => [p.id, p]));
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex flex-wrap gap-2">
        {value.map((t, i) => (
          <span key={t.person_id} className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-700 text-xs pl-2 pr-1 py-1 rounded" data-testid={`${testid}-member-${i}`}>
            <span className={byId[t.person_id]?.active === false ? "line-through" : ""}>{byId[t.person_id]?.name || t.name || "Unknown"}</span>
            <input value={t.role || ""} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, role: e.target.value } : x)))} placeholder="role" list="team-role-suggestions"
              className="h-5 w-[110px] text-[11px] px-1 rounded border border-slate-200 bg-white" data-testid={`${testid}-role-${i}`} />
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600" data-testid={`${testid}-remove-${i}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
        {value.length === 0 && <span className="text-xs text-slate-400">No team members yet.</span>}
      </div>
      <datalist id="team-role-suggestions">{["Medical Writer", "Reviewer", "Analyst", "Coordinator", "Health Economist", "Statistician", "QC"].map((r) => <option key={r} value={r} />)}</datalist>
      <PersonPicker people={available.filter((p) => !exclude.includes(p.id))} value={pick} onChange={(id) => { if (id) { onChange([...value, { person_id: id, role: "" }]); setPick(null); } }}
        placeholder="+ Add team member" onAdded={onAdded} testid={`${testid}-add`} className="h-8 text-xs" />
    </div>
  );
};
